import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

// URL utility types and functions
interface URLParams {
  port?: number;
}

export const fetchBaseUrl = ({ port }: URLParams) => {
  let baseURL;
  const url = window.location.origin;
  
  if (url.includes("localhost")) {
    if (port != undefined && port != null) {
      baseURL = `http://localhost:${port}`;
    } else {
      baseURL = window.location.origin;
    }
  } else {
    baseURL = url;
  }
  return baseURL;
};

// Define API base URL - this should be configurable via environment variable
// For local development, point to the backend server port
// Try to determine if we're running inside Docker or locally
const isDocker = process.env.REACT_APP_DOCKER === 'true';

let API_BASE_URL = "";

// Default fallbacks using dynamic URL detection
if (isDocker) {
  // Inside Docker, use the service name
  API_BASE_URL = 'http://pen-test-handler:8088';
} else {
  // Use dynamic base URL with pentest port for local development
  API_BASE_URL = fetchBaseUrl({ port: 8088 });
}

/**
 * Log detailed error information for debugging
 */
const logErrorDetails = (error: AxiosError, method: string, url: string) => {
  console.error(`${method} ${url} failed:`, error);
  
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('Response data:', error.response.data);
    console.error('Response status:', error.response.status);
    console.error('Response headers:', error.response.headers);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('No response received. Request details:', error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('Error setting up request:', error.message);
  }
  
  console.error('Request config:', error.config);
};

/**
 * Core API client for making HTTP requests to the backend
 */
class ApiClient {
  private client: AxiosInstance;
  private static tokenProvider: (() => Promise<string | null>) | null = null;
  private onAuthFailureCallback: (() => void) | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      // Default timeout for regular requests
      timeout: 15000,
      // Disable withCredentials to avoid CORS issues with simple requests
      withCredentials: false,
    });
    
    // Add request interceptor for authentication
    this.client.interceptors.request.use(
      async (config: any) => {
        try {
          // Get token using the tokenProvider if available
          const token = ApiClient.tokenProvider ? await ApiClient.tokenProvider() : null;
          
          if (token) {
            config.headers = {
              ...config.headers,
              Authorization: token,
            };
            console.debug(`ApiClient: Using token for request to ${config.url}`);
          } else {
            console.warn('ApiClient: No valid token available for request');
            // Don't fail the request here, let the backend handle authentication
          }
          // Session activity is handled by the main app's auth context
          
        } catch (err) {
          console.error("ApiClient: Failed to get token", err);
          // Don't fail the request, let it proceed and handle auth failure in response interceptor
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    
    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        console.debug(`ApiClient: Received response from ${response.config.url}:`, response.status);
        return response;
      },
      (error: AxiosError) => {
        // Handle specific error cases
        if (error.response?.status === 401) {
          console.error('ApiClient: Unauthorized access - handling authentication failure');
          this.handleAuthFailure();
        } else if (error.response?.status === 403) {
          console.error('ApiClient: Forbidden access - possible session/permission issue');
          this.handleAuthFailure();
        } else if (error.response?.status === 500) {
          console.error('ApiClient: Server error. Please try again later or contact support.');
        } else if (error.code === 'ECONNABORTED') {
          const isStreamingRequest = error.config?.url?.includes('/chat/completion');
          if (isStreamingRequest) {
            console.error('ApiClient: Chat request timeout. The scan is taking longer than expected. Please wait and check the results later.');
          } else {
            console.error('ApiClient: Request timeout. Please check your connection and try again.');
          }
        } else if (error.message.includes('Network Error')) {
          console.error('ApiClient: Network error. Please check your connection to the server.');
        }
        
        // Log detailed error information
        if (error.config) {
          logErrorDetails(error, error.config.method?.toUpperCase() || 'UNKNOWN', error.config.url || 'UNKNOWN');
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Handle authentication failure
   */
  private handleAuthFailure(): void {
    console.error('ApiClient: Authentication failed');
    
    if (this.onAuthFailureCallback) {
      this.onAuthFailureCallback();
    } else {
      // If no callback is set, log the auth failure
      console.log('ApiClient: No auth failure callback set, auth failure not handled automatically');
    }
  }

  /**
   * Set callback for authentication failures
   */
  setAuthFailureCallback(callback: () => void): void {
    this.onAuthFailureCallback = callback;
  }

  static setTokenProvider(provider: () => Promise<string | null>) {
    ApiClient.tokenProvider = provider;
  }

  /**
   * Make a GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.get(url, config);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`GET ${url} failed:`, error.message);
      }
      throw error;
    }
  }

  /**
   * Make a POST request
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      // Check if this is a chat completion request and extend timeout
      const isStreamingRequest = url.includes('/chat/completion');
      const requestConfig: AxiosRequestConfig = {
        ...config,
        // Use longer timeout for streaming/chat requests (3 minutes)
        timeout: isStreamingRequest ? 180000 : (config?.timeout || 15000),
      };
      
      const response: AxiosResponse<T> = await this.client.post(url, data, requestConfig);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`POST ${url} failed:`, error.message);
      }
      throw error;
    }
  }
  
  /**
   * Make a POST request with FormData (for file uploads) with extended timeout
   */
  async postForm<T>(url: string, data: FormData, config?: AxiosRequestConfig): Promise<T> {
    try {
      const formConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          ...config?.headers,
          'Content-Type': 'multipart/form-data',
        },
        // Use longer timeout for file uploads (120 seconds)
        timeout: 120000,
      };
      
      const response: AxiosResponse<T> = await this.client.post(url, data, formConfig);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`POST form ${url} failed:`, error.message);
      }
      throw error;
    }
  }

  /**
   * Make a PUT request
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.put(url, data, config);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`PUT ${url} failed:`, error.message);
      }
      throw error;
    }
  }

  /**
   * Make a DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.delete(url, config);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`DELETE ${url} failed:`, error.message);
      }
      throw error;
    }
  }
  
  /**
   * Make a POST request with extended timeout for long operations
   */
  async postWithTimeout<T>(url: string, data?: any, timeoutMs: number = 15000, config?: AxiosRequestConfig): Promise<T> {
    try {
      const requestConfig: AxiosRequestConfig = {
        ...config,
        timeout: timeoutMs,
      };
      
      const response: AxiosResponse<T> = await this.client.post(url, data, requestConfig);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`POST ${url} with timeout ${timeoutMs}ms failed:`, error.message);
      }
      throw error;
    }
  }

  /**
   * Get base URL
   */
  getBaseUrl(): string {
    return API_BASE_URL || 'http://localhost:8088';
  }

  /**
   * Make a GET request for blob data (files) with extended timeout
   */
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await this.client.get(url, {
        ...config,
        responseType: 'blob',
        // Use longer timeout for file operations (60 seconds)
        timeout: 60000,
      });
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`GET blob ${url} failed:`, error.message);
      }
      throw error;
    }
  }

  /**
   * Make a GET request for file content with extended timeout
   */
  async getFileContent<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.get(url, {
        ...config,
        // Use longer timeout for file content operations (60 seconds)
        timeout: 60000,
      });
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(`GET file content ${url} failed:`, error.message);
      }
      throw error;
    }
  }
}

// Export singleton instance and class
export const apiClient = new ApiClient();
export { ApiClient };
export default apiClient;
